import { useState, useEffect } from 'react';
import { X, Sparkles, MessageSquare, FileText, Languages, HelpCircle, Wand2, Loader2 } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { extractText } from '@/lib/aiApi';
import AiPaywall from './AiPaywall';
import SignInRequired from '../SignInRequired';
import ChatTab from './ChatTab';
import SummaryTab from './SummaryTab';
import TranslateTab from './TranslateTab';
import QuestionsTab from './QuestionsTab';
import AssistantTab from './AssistantTab';

const TABS = [
  { id: 'assistant', label: 'Assistant', Icon: Wand2, Component: AssistantTab },
  { id: 'chat', label: 'Chat', Icon: MessageSquare, Component: ChatTab },
  { id: 'summary', label: 'Summary', Icon: FileText, Component: SummaryTab },
  { id: 'translate', label: 'Translate', Icon: Languages, Component: TranslateTab },
  { id: 'questions', label: 'Questions', Icon: HelpCircle, Component: QuestionsTab },
];

export default function AiPanel({ open, onClose, file, onAction }) {
  const { user, account } = useAuth();
  const [activeTab, setActiveTab] = useState('assistant');
  const [documentHash, setDocumentHash] = useState(null);
  const [extracting, setExtracting] = useState(false);
  const [extractError, setExtractError] = useState(null);
  const [pageCount, setPageCount] = useState(null);

  const hasAi = account?.capabilities?.ai_tools;
  const tier = account?.tier || 'free';

  // Extract once on first open / file change. Downstream tabs then reuse documentHash.
  useEffect(() => {
    if (!open || !file || !hasAi) return;
    setDocumentHash(null);
    setExtractError(null);
    setExtracting(true);
    extractText(file)
      .then((data) => {
        setDocumentHash(data.documentHash);
        setPageCount(data.pageCount);
      })
      .catch((err) => setExtractError(err.message || 'Extraction failed.'))
      .finally(() => setExtracting(false));
  }, [open, file, hasAi]);

  if (!open) return null;

  const ActiveComponent = TABS.find((t) => t.id === activeTab)?.Component;
  const tabProps = documentHash ? { documentHash, onAction } : { file, onAction };

  return (
    <div className="fixed inset-y-0 right-0 z-40 w-full max-w-md bg-white border-l border-gray-200 shadow-xl flex flex-col">
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
        <div className="flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-indigo-600" />
          <h2 className="font-semibold">AI tools</h2>
          {pageCount && hasAi && (
            <span className="text-xs text-gray-500">· {pageCount} page{pageCount === 1 ? '' : 's'}</span>
          )}
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close AI panel"
          className="rounded p-1 hover:bg-gray-100"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {!user ? (
        // Logged-out (e.g. a licensed desktop user who signed out): AI needs a
        // live session — show sign-in, never a "Subscribe" pitch.
        <SignInRequired feature="AI tools" compact />
      ) : !hasAi ? (
        <AiPaywall tier={tier} stripeEnabled={Boolean(account?.stripeEnabled)} />
      ) : (
        <>
          <nav className="flex border-b border-gray-200 px-1 overflow-x-auto">
            {TABS.map(({ id, label, Icon }) => (
              <button
                key={id}
                type="button"
                onClick={() => setActiveTab(id)}
                className={`flex items-center gap-1 px-3 py-2 text-xs font-medium whitespace-nowrap border-b-2 -mb-px ${
                  activeTab === id
                    ? 'border-indigo-600 text-indigo-700'
                    : 'border-transparent text-gray-600 hover:text-gray-900'
                }`}
              >
                <Icon className="w-3.5 h-3.5" />
                {label}
              </button>
            ))}
          </nav>
          <div className="flex-1 overflow-hidden">
            {extracting && (
              <div className="flex items-center justify-center h-full text-sm text-gray-500 gap-2">
                <Loader2 className="w-4 h-4 animate-spin" />
                Preparing document…
              </div>
            )}
            {extractError && !extracting && (
              <div className="p-4 text-sm text-red-600">{extractError}</div>
            )}
            {!extracting && !extractError && ActiveComponent && (
              <ActiveComponent {...tabProps} />
            )}
          </div>
        </>
      )}
    </div>
  );
}
